<template>
  <div id="selection-list" class="relative" :class="[classes]">
    <div class="flex items-center w-full space-x-1 overflow-visible">
      <label
        v-if="showLabel && !floatingLabel"
        :class="[
          labelClasses,
          !isBoxBackground ? 'bg-white dark:bg-dark-screen-background' : 'bg-light-box-background dark:bg-dark-box-background',
          state.isInputFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
        ]"
        class="z-[1] m-0.5 rounded-md self-stretch text-md text-common-grey transform transition-all left-2 ease-in-out duration-side-menu-transition-duration w-full"
        :for="name">
        {{ label }}
      </label>
      <Listbox
        :model-value="modelValue"
        :disabled="disabled"
        as="div"
        :class="[listboxClasses]"
        class="w-full text-light-body-text dark:text-dark-body-text"
        name="type"
        @update:modelValue="(value) => handleSelectChange(value)">
        <ListboxLabel
          v-if="showLabel && floatingLabel"
          :class="[
            state.isInputFocused || selected_item ? '-top-3' : 'top-[10px]',
            !isBoxBackground ? 'bg-white dark:bg-dark-screen-background' : 'bg-light-box-background dark:bg-dark-box-background',
            state.isInputFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
          ]"
          class="z-[1] text-base font-light text-md text-common-grey absolute transform transition-all left-2 ease-in-out duration-side-menu-transition-duration"
          :for="name">
          {{ label }}
        </ListboxLabel>
        <div class="relative">
          <div v-if="inlineLabel" id="listbox-button">
            <ListboxButton
              :class="[
                errorMessage || (error && !value)
                  ? 'border-status-error-red text-status-error-red placeholder-status-error-red focus:outline-none focus:ring-status-error-red focus:border-status-error-red'
                  : '',
                disabled ? 'text-gray-400' : '',
                meta.valid ? 'success' : '',
                'font-normal appearance-none block w-full h-10 px-4 py-2 pr-8 border border-light-divider-border dark:border-dark-divider-border rounded-lg shadow-sm focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:ring-1 focus:border-light-brand-blue dark:focus:border-dark-brand-blue text-sm transition ease-in-out duration-side-menu-transition-duration',
                isBoxBackground ? 'bg-light-box-background dark:bg-dark-box-background' : 'bg-white dark:bg-dark-screen-background',
                inputClasses
              ]"
              @change="handleChange"
              @blur="handleBlur"
              @focusin="focusInput"
              @focusout="unfocusInput">
              <span class="inline-flex items-center w-full truncate">
                <slot name="prefix" />
                <component
                  :is="getSelectedItemIcon()"
                  v-if="getSelectedItemIcon()"
                  class="w-5 h-5 mr-3 transition ease-in-out duration-side-menu-transition-duration"
                  aria-hidden="true" />

                <span class="text-gray-600 truncate dark:text-light-divider">{{ label }}</span>

                <span v-if="selected_item && labelAttr" class="ml-2 text-gray-500 truncate">{{ getSelectedItemLabel() }}</span>
                <span v-else-if="selected_item && !labelAttr" class="block mx-1 text-base truncate">{{ selected_item }}</span>
                <span v-else class="ml-2 text-gray-400 truncate">{{ placeholder }}</span>
              </span>
              <span v-if="!errorMessage" class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
          </div>
          <div v-else>
            <ListboxButton
              id="listbox-button-2"
              class="flex"
              :class="[
                errorMessage || (error && !value)
                  ? 'border-status-error-red text-status-error-red placeholder-status-error-red focus:outline-none focus:ring-status-error-red focus:border-status-error-red'
                  : '',
                disabled ? 'text-gray-400' : '',
                meta.valid ? 'success' : '',
                'text-left font-normal appearance-none block w-full h-10 px-4 py-2 pr-8 border border-light-divider-border dark:border-dark-divider-border rounded-lg shadow-sm focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:ring-1 focus:border-light-brand-blue dark:focus:border-dark-brand-blue text-sm transition ease-in-out duration-side-menu-transition-duration',
                isBoxBackground ? 'bg-light-box-background dark:bg-dark-box-background' : 'bg-white dark:bg-dark-screen-background',
                inputClasses
              ]"
              @mousedown.stop
              @change="handleChange"
              @blur="handleBlur"
              @focusin="focusInput"
              @focusout="unfocusInput">
              <span class="inline-flex items-center w-full truncate">
                <slot name="prefix" />
                <component
                  :is="getSelectedItemIcon()"
                  v-if="getSelectedItemIcon()"
                  class="inline w-5 h-5 mr-3 transition ease-in-out duration-side-menu-transition-duration"
                  aria-hidden="true" />

                <span v-if="selected_item && labelAttr" class="block mx-1 text-base truncate">{{ getSelectedItemLabel() }}</span>
                <span v-else-if="selected_item && !labelAttr" class="block mx-1 text-base truncate">{{ selected_item }} </span>
                <span v-else class="block mx-1 text-base truncate text-common-grey">{{ placeholder }}</span>
              </span>
              <span v-if="!errorMessage" class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <ChevronDown class="w-5 h-5 text-common-grey" aria-hidden="true" />
              </span>
            </ListboxButton>
          </div>
          <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <ListboxOptions
              :class="[
                { 'z-[1050]': true },
                { 'bg-white dark:bg-dark-screen-background': !isBoxBackground },
                { '!-mt-44': openToTop },
                { 'bg-light-box-background dark:bg-dark-box-background': isBoxBackground },
                { relative: relativePos },
                { 'max-h-96': organizationSelector }
              ]"
              class="absolute z-30 mt-1 w-full shadow-lg max-h-40 rounded-lg text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none transition-ease-in-out duration-side-menu-transition-duration">
              <ListboxOption v-if="(selected_item && allowNull) || isBulkMetric" key="reset" v-slot="{ active }" :value="null">
                <li
                  :class="[
                    active
                      ? 'text-dark-body-text bg-light-brand-blue dark:text-light-body-text dark:bg-dark-brand-blue'
                      : 'text-light-body-text dark:text-dark-body-text',
                    'cursor-default relative py-2 pl-4 pr-4 rounded-lg   hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue transition ease-in-out duration-500'
                  ]">
                  <span class="block font-light truncate opacity-0">
                    {{ label }}
                  </span>
                </li>
              </ListboxOption>
              <ListboxOption v-if="actionName" as="template">
                <li class="relative py-2 pl-4 pr-4 transition duration-500 ease-in-out rounded-lg dark:text-light-brand-blue">
                  <span class="cursor-pointer" @click="startAction">
                    {{ actionName }}
                  </span>

                  <span
                    class="text-light-brand-blue dark:text-dark-brand-blue absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration">
                    <Plus class="w-5 h-5" aria-hidden="true" />
                  </span>
                </li>
              </ListboxOption>
              <ListboxOption
                v-for="item in items.filter((i) => i)"
                :key="item"
                v-slot="{ active, selected }"
                as="template"
                :value="!returnIds ? item : valueAttr ? item[valueAttr] : item">
                <li
                  :class="[
                    active
                      ? 'text-dark-body-text bg-light-brand-blue dark:text-light-body-text dark:bg-dark-brand-blue'
                      : 'text-light-body-text dark:text-dark-body-text',
                    'cursor-default relative py-2 pl-8 has-tooltip',
                    listItemClasses
                  ]"
                  class="transition duration-500 ease-in-out rounded-lg hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue">
                  <div class="flex items-center w-full pr-1">
                    <component
                      :is="item.icon"
                      v-if="item.icon"
                      class="inline w-5 h-5 mr-3 transition ease-in-out duration-side-menu-transition-duration"
                      aria-hidden="true" />

                    <span
                      v-if="organizationSelector"
                      class="px-1 py-1 -ml-1 mr-2 text-sm bg-gray-200 rounded-lg"
                      :class="[active ? 'text-gray-700' : 'text-gray-500']">
                      <Building2 class="w-4 h-4" />
                    </span>

                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate might-overflow']">
                      {{ labelAttr ? item[labelAttr] : item }}
                    </span>
                    <span
                      v-if="showObjectType"
                      class="px-1 py-1 ml-1 text-sm bg-gray-200 rounded-lg capitalize"
                      :class="[active ? 'text-gray-700' : '']">{{ item.object_type }}</span>
                  </div>

                  <span
                    v-if="selected"
                    :class="[
                      active ? 'text-light-body-text dark:text-dark-body-text' : 'text-light-brand-blue dark:text-dark-brand-blue',
                      'absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration'
                    ]">
                    <Check class="w-5 h-5" aria-hidden="true" />
                  </span>
                </li>
              </ListboxOption>

              <div v-if="organizationSelector" :value="null" @click="$emitter.emit(ALL_EMITTERS.CHANGE_ORGANIZATION)">
                <li
                  :class="[
                    'text-light-body-text dark:text-dark-body-text',
                    'cursor-default relative py-2 pl-4 pr-4 rounded-lg    dark:hover:bg-dark-brand-blue transition ease-in-out duration-500'
                  ]">
                  <div
                    class="bg-gradient-to-r cursor-pointer rounded-md p-2 from-[#2DA7D7] to-[#2DA7D7] text-white flex items-center space-x-4">
                    <svg width="26"
                         height="24"
                         viewBox="0 0 26 24"
                         fill="none"
                         xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_3462_6030)">
                        <g clip-path="url(#clip1_3462_6030)">
                          <g clip-path="url(#clip2_3462_6030)">
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M23.0764 12C23.0764 14.5859 22.0057 17.0658 20.1 18.8943C18.1942 20.7228 15.6095 21.75 12.9143 21.75C10.2192 21.75 7.63445 20.7228 5.7287 18.8943C3.82295 17.0658 2.75231 14.5859 2.75231 12C2.75231 9.41414 3.82295 6.93419 5.7287 5.10571C7.63445 3.27723 10.2192 2.25 12.9143 2.25C15.6095 2.25 18.1942 3.27723 20.1 5.10571C22.0057 6.93419 23.0764 9.41414 23.0764 12ZM25.4215 12C25.4215 15.1826 24.1038 18.2348 21.7582 20.4853C19.4127 22.7357 16.2314 24 12.9143 24C9.59725 24 6.41602 22.7357 4.07048 20.4853C1.72494 18.2348 0.407227 15.1826 0.407227 12C0.407227 8.8174 1.72494 5.76516 4.07048 3.51472C6.41602 1.26428 9.59725 0 12.9143 0C16.2314 0 19.4127 1.26428 21.7582 3.51472C24.1038 5.76516 25.4215 8.8174 25.4215 12ZM12.9143 15.375C12.9143 15.9717 12.6673 16.544 12.2275 16.966C11.7877 17.3879 11.1912 17.625 10.5693 17.625C9.94731 17.625 9.35082 17.3879 8.91104 16.966C8.47125 16.544 8.22418 15.9717 8.22418 15.375C8.22418 14.7783 8.47125 14.206 8.91104 13.784C9.35082 13.3621 9.94731 13.125 10.5693 13.125C11.1912 13.125 11.7877 13.3621 12.2275 13.784C12.6673 14.206 12.9143 14.7783 12.9143 15.375ZM7.4675 12C6.90779 12.4738 6.47403 13.069 6.20227 13.736C5.93051 14.403 5.8286 15.1226 5.905 15.835C5.9814 16.5473 6.23392 17.232 6.64155 17.8319C7.04919 18.4318 7.60018 18.9297 8.24872 19.2843C8.89726 19.6388 9.62463 19.8397 10.3704 19.8702C11.1161 19.9008 11.8587 19.7602 12.5364 19.46C13.2141 19.1598 13.8073 18.7088 14.2668 18.1444C14.7263 17.58 15.0387 16.9185 15.1781 16.215C15.8876 16.4695 16.6508 16.5554 17.4024 16.4653C18.154 16.3751 18.8717 16.1117 19.4941 15.6974C20.1165 15.2832 20.625 14.7305 20.9762 14.0866C21.3274 13.4427 21.5109 12.7267 21.5109 12C21.5109 11.2733 21.3274 10.5573 20.9762 9.91341C20.625 9.26951 20.1165 8.71681 19.4941 8.30257C18.8717 7.88833 18.154 7.62488 17.4024 7.53474C16.6508 7.44461 15.8876 7.53048 15.1781 7.785C15.0387 7.08145 14.7263 6.41999 14.2668 5.8556C13.8073 5.29121 13.2141 4.84019 12.5364 4.54002C11.8587 4.23985 11.1161 4.0992 10.3704 4.12976C9.62463 4.16033 8.89726 4.36122 8.24872 4.71574C7.60018 5.07027 7.04919 5.56818 6.64155 6.16811C6.23392 6.76804 5.9814 7.45265 5.905 8.16504C5.8286 8.87744 5.93051 9.59704 6.20227 10.264C6.47403 10.931 6.90779 11.5262 7.4675 12ZM10.5693 10.875C11.1912 10.875 11.7877 10.6379 12.2275 10.216C12.6673 9.79403 12.9143 9.22174 12.9143 8.625C12.9143 8.02826 12.6673 7.45597 12.2275 7.03401C11.7877 6.61205 11.1912 6.375 10.5693 6.375C9.94731 6.375 9.35082 6.61205 8.91104 7.03401C8.47125 7.45597 8.22418 8.02826 8.22418 8.625C8.22418 9.22174 8.47125 9.79403 8.91104 10.216C9.35082 10.6379 9.94731 10.875 10.5693 10.875ZM16.8228 14.25C17.4448 14.25 18.0413 14.0129 18.481 13.591C18.9208 13.169 19.1679 12.5967 19.1679 12C19.1679 11.4033 18.9208 10.831 18.481 10.409C18.0413 9.98705 17.4448 9.75 16.8228 9.75C16.2009 9.75 15.6044 9.98705 15.1646 10.409C14.7248 10.831 14.4777 11.4033 14.4777 12C14.4777 12.5967 14.7248 13.169 15.1646 13.591C15.6044 14.0129 16.2009 14.25 16.8228 14.25Z"
                              fill="white" />
                          </g>
                        </g>
                      </g>
                      <defs>
                        <clipPath id="clip0_3462_6030">
                          <rect width="25.0142" height="24" fill="white" transform="translate(0.407227)" />
                        </clipPath>
                        <clipPath id="clip1_3462_6030">
                          <rect width="25.0142" height="24" fill="white" transform="translate(0.407227)" />
                        </clipPath>
                        <clipPath id="clip2_3462_6030">
                          <rect width="25.0142" height="24" fill="white" transform="translate(0.407227)" />
                        </clipPath>
                      </defs>
                    </svg>

                    <div>{{ $t("Add or join") }}</div>
                  </div>
                </li>
              </div>
            </ListboxOptions>
          </transition>

          <div v-show="errorMessage || (error && !value)" class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <CircleX class="w-6 h-6 text-red-400" />
          </div>
        </div>
      </Listbox>

      <p
        class="absolute right-0 text-red-400 first-letter:capitalize top-10 text-xsm"
        :class="[errorMessage || meta.valid || (error && !value) ? 'visible' : 'invisible']">
        {{ errorMessage || successMessage || (error && !value ? error : "") }}
      </p>
    </div>
  </div>
</template>

<script>
import { useField } from "vee-validate"
import { reactive, toRef } from "vue"
import { Building2, ChevronDown, Check, Plus, CircleX } from "lucide-vue-next"
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import PlaybookSequenceIcon from "./icons/PlaybookSequenceIcon.vue"
import PlaybookAuditIcon from "./icons/PlaybookAuditIcon.vue"
import DotIcon from "./icons/DotIcon.vue"
import RedDotIcon from "./icons/RedDotIcon.vue"
export default {
  components: {
    Building2,
    CircleX,
    DotIcon,
    RedDotIcon,
    Listbox,
    ListboxLabel,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    ChevronDown,
    Check,
    Plus,
    PlaybookSequenceIcon,
    PlaybookAuditIcon
  },
  props: {
    organizationSelector: null,
    relativePos: null,
    openToTop: null,
    actionName: {
      type: String,
      required: false
    },
    error: {
      type: String,
      default: "",
      required: false
    },
    items: {
      type: Array,
      required: true
    },
    showLabel: {
      type: Boolean,
      default: false
    },
    floatingLabel: {
      type: Boolean,
      default: true
    },
    inputClasses: null,
    classes: null,
    labelClasses: {
      type: String,
      default: ""
    },
    listboxClasses: {
      type: String,
      default: ""
    },
    listItemClasses: {
      type: String,
      default: ""
    },
    value: {
      default: null
    },
    // eslint-disable-next-line vue/no-unused-properties
    modelValue: null,
    name: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: false
    },
    labelAttr: {
      type: String,
      validator(v) {
        return v !== undefined
      }
    },
    valueAttr: {
      validator(v) {
        return v !== undefined
      }
    },
    disabled: {
      type: Boolean,
      required: false
    },
    returnIds: {
      type: Boolean,
      default: true
    },
    successMessage: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: ""
    },
    allowNull: {
      type: Boolean,
      default: true
    },
    inlineLabel: {
      type: Boolean,
      default: false
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    clearSelectedItem: {
      type: Boolean,
      default: false
    },
    showObjectType: {
      type: Boolean,
      default: false
    },
    isBulkMetric: {
      type: Boolean,
      default: false
    }
  },

  emits: ["update:currentItem", "update:modelValue"],
  setup(props) {
    const state = reactive({ isInputFocused: false })
    const name = toRef(props, "name")
    let init_values = [name]
    if (props.value) {
      init_values = [
        name,
        undefined,
        {
          initialValue: props.value
        }
      ]
    }
    const { value: selected_item, errorMessage, handleBlur, handleChange, meta } = useField(...init_values)
    return {
      handleChange,
      handleBlur,
      errorMessage,
      selected_item,
      meta,
      state
    }
  },
  data() {
    return {
      ALL_EMITTERS
    }
  },
  watch: {
    value() {
      if (this.value != this.selected_item) {
        this.handleChange(this.value)
        this.$emit("update:currentItem", this.value)
        this.$emit("update:modelValue", this.value)
      }
    },
    selected_item() {
      if (this.clearSelectedItem) {
        this.selected_item = null
      }
    }
  },
  created() {
    if (this.modelValue) {
      this.selected_item = this.modelValue
    }
  },
  methods: {
    handleSelectChange(value) {
      if (value != this.selected_item) {
        this.selected_item = value
        this.$emit("update:currentItem", value)
        this.$emit("update:modelValue", value)
      }
    },
    focusInput() {
      this.state.isInputFocused = true
    },
    unfocusInput() {
      this.state.isInputFocused = false
    },
    getSelectedItemLabel() {
      try {
        if (this.selected_item) {
          if (typeof this.selected_item != "object") {
            return this.items.find((d) => d[this.valueAttr] == this.selected_item)[this.labelAttr]
          } else {
            return this.selected_item[this.labelAttr]
          }
        } else {
          return ""
        }
      } catch (error) {
        return ""
      }
    },

    getSelectedItemIcon() {
      try {
        if (this.selected_item) {
          if (typeof this.selected_item != "object") {
            return this.items.find((d) => d[this.valueAttr] == this.selected_item)?.icon
          } else {
            return this.selected_item?.icon
          }
        } else {
          return ""
        }
      } catch (error) {
        return ""
      }
    },

    startAction() {
      if (this.actionName === this.$t("Create new goal")) {
        this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, { records: [], record: this.$store.state.ui.currentCompany })
      } else if (this.actionName === this.$t("Create new task")) {
        this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, { records: [], record: this.$store.state.ui.currentCompany })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.might-overflow {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.might-overflow:hover {
  text-overflow: clip;
  white-space: normal;
  word-break: break-all;
}
</style>
